import { useQuery, type ApolloError } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { type SelectOption } from '../../../../../../components';

export type DepartmentType = {
  value: string;
  label: string;
  researchAreas: string[];
  proposedChiefSupervisors: string[];
};

export type ProgrammeChoiceMetadata = {
  departments: DepartmentType[];
  entryYears: string[];
  entryYearList: SelectOption[];
  prgCollabPhdPartnerList: SelectOption[];
  rpgJoinPhdPartnerList: SelectOption[];
};

type GetChoiceData = {
  programmeChoiceMetadata: ProgrammeChoiceMetadata;
  programmeChoiceEntryYears: SelectOption[];
  loading: boolean;
  error: ApolloError | undefined;
};

type GetTableFilterData = {
  departments: DepartmentType[];
  entryYears: string[];
  entryYearList: SelectOption[];
  countryList: SelectOption[];
  loading: boolean;
  error: ApolloError | undefined;
};
export const useGetChoiceData = (): GetChoiceData => {
  const { data, loading, error } = useQuery<GetChoiceData>(
    gql`
      query {
        programmeChoiceMetadata {
          departments {
            value
            label
          }
          entryYears
          entryYearList {
            value
            label
          }
          rpgJoinPhdPartnerList {
            value
            label
          }
          prgCollabPhdPartnerList {
            value
            label
          }
        }
        programmeChoiceEntryYears {
          value
          label
          shouldShow
        }
      }
    `,
    {
      fetchPolicy: 'network-only',
    },
  );

  const programmeChoiceMetadata: ProgrammeChoiceMetadata =
    data?.programmeChoiceMetadata ?? {
      departments: [],
      entryYears: [],
      entryYearList: [],
      prgCollabPhdPartnerList: [],
      rpgJoinPhdPartnerList: [],
    };

  const programmeChoiceEntryYears: SelectOption[] =
    data?.programmeChoiceEntryYears?.map(item => ({
      value: item.value,
      label: item.label ?? String(item.value),
      shouldShow: item.shouldShow,
    })) ?? [];

  return {
    programmeChoiceMetadata,
    programmeChoiceEntryYears,
    loading,
    error,
  };
};

export const useTableFilterData = (): GetTableFilterData => {
  const { data, loading, error } = useQuery(
    gql`
      query {
        programmeChoiceMetadata {
          departments {
            value
            label
          }
          countryList {
            value
            label
          }
          entryYears
          entryYearList {
            value
            label
          }
        }
      }
    `,
    {
      fetchPolicy: 'network-only',
    },
  );

  const departments: DepartmentType[] =
    data?.programmeChoiceMetadata?.departments ?? [];
  const entryYears: string[] = data?.programmeChoiceMetadata?.entryYears ?? [];
  const entryYearList: SelectOption[] =
    data?.programmeChoiceMetadata?.entryYearList ?? [];
  const countryList: SelectOption[] =
    data?.programmeChoiceMetadata?.countryList ?? [];

  return {
    departments,
    entryYears,
    entryYearList,
    countryList,
    loading,
    error,
  };
};
